(() => {
  const instances = new Map();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const modulo = (value, length) => ((value % length) + length) % length;

  const stripBlockOwnership = (node) => {
    node.removeAttribute('id');
    node.removeAttribute('data-block-id');
    node.removeAttribute('data-shopify-editor-block');
    node.querySelectorAll('[id], [data-block-id], [data-shopify-editor-block]').forEach((element) => {
      element.removeAttribute('id');
      element.removeAttribute('data-block-id');
      element.removeAttribute('data-shopify-editor-block');
    });
    node.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
  };

  const prepareBoundaryCloneMedia = (clone, original) => {
    const originalImages = Array.from(original.querySelectorAll('img'));
    clone.querySelectorAll('source').forEach((source) => source.remove());
    clone.querySelectorAll('img').forEach((image, index) => {
      const originalImage = originalImages[index];
      const resolvedSource = originalImage?.currentSrc || originalImage?.src;
      if (resolvedSource) image.src = resolvedSource;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.loading = 'eager';
      image.decoding = 'async';
      image.fetchPriority = 'high';
    });
  };

  const waitForImage = (image) => {
    if (image.complete) return image.decode?.().catch(() => {}) || Promise.resolve();
    return new Promise((resolve) => {
      image.addEventListener('load', resolve, { once: true });
      image.addEventListener('error', resolve, { once: true });
    }).then(() => image.decode?.().catch(() => {}));
  };

  class NasCarousel {
    constructor(root) {
      this.root = root;
      this.section = root.closest('[data-nas-hardware]');
      this.sectionId = this.section?.dataset.sectionId;
      this.viewport = root.querySelector('[data-nas-viewport]');
      this.track = root.querySelector('[data-nas-track]');
      this.previousButton = root.querySelector('[data-nas-prev]');
      this.nextButton = root.querySelector('[data-nas-next]');
      this.currentStatus = root.querySelector('[data-nas-current]');
      this.realSlides = Array.from(root.querySelectorAll('[data-nas-slide]:not([data-clone])'));
      this.realCount = this.realSlides.length;
      this.realIndex = this.realCount > 1 ? 1 : 0;
      this.physicalIndex = this.realCount > 1 ? this.realIndex + 1 : 0;
      this.queue = [];
      this.moving = false;
      this.destroyed = false;
      this.pointerId = null;
      this.pointerStartX = 0;
      this.pointerStartY = 0;
      this.pointerDeltaX = 0;
      this.pointerDeltaY = 0;
      this.autoplayDelay = Math.max(3000, Number(root.dataset.autoplayDelay) || 5000);
      this.autoplayEnabled = root.dataset.autoplay === 'true' && !reduceMotion.matches;
      this.autoplayTimer = null;
      this.hovered = false;
      this.focusWithin = false;
      this.resizePending = false;
      this.boundaryReady = this.realCount < 2;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      if (!this.viewport || !this.track || this.realCount === 0) return;

      if (this.realCount > 1) this.createBoundaryClones();
      this.slides = Array.from(this.track.children);
      this.bind();
      this.resizeObserver = new ResizeObserver(() => this.handleResize());
      this.resizeObserver.observe(this.viewport);
      this.update(false);
      if (this.realCount > 1) {
        this.preloadBoundaryClones().finally(() => {
          if (this.destroyed) return;
          this.boundaryReady = true;
          root.classList.add('is-ready');
          this.consumeQueue();
          this.startAutoplay();
        });
      } else {
        root.classList.add('is-ready');
        this.startAutoplay();
      }
    }

    createBoundaryClones() {
      const firstClone = this.realSlides[0].cloneNode(true);
      const lastClone = this.realSlides[this.realCount - 1].cloneNode(true);
      [firstClone, lastClone].forEach((clone) => {
        stripBlockOwnership(clone);
        clone.dataset.clone = '';
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
      });
      prepareBoundaryCloneMedia(lastClone, this.realSlides[this.realCount - 1]);
      prepareBoundaryCloneMedia(firstClone, this.realSlides[0]);
      this.track.prepend(lastClone);
      this.track.append(firstClone);
    }

    preloadBoundaryClones() {
      const images = Array.from(this.track.querySelectorAll('[data-clone] img'));
      return Promise.race([
        Promise.all(images.map(waitForImage)),
        new Promise((resolve) => window.setTimeout(resolve, 4000)),
      ]);
    }

    bind() {
      const options = { signal: this.signal };
      this.previousButton?.addEventListener('click', () => this.enqueue(-1), options);
      this.nextButton?.addEventListener('click', () => this.enqueue(1), options);
      this.track.addEventListener('transitionend', (event) => this.handleTransitionEnd(event), options);
      this.viewport.addEventListener('pointerdown', (event) => this.handlePointerDown(event), options);
      this.viewport.addEventListener('pointermove', (event) => this.handlePointerMove(event), options);
      this.viewport.addEventListener('pointerup', (event) => this.handlePointerEnd(event), options);
      this.viewport.addEventListener('pointercancel', (event) => this.handlePointerEnd(event), options);
      this.root.addEventListener('mouseenter', () => {
        this.hovered = true;
        this.stopAutoplay();
      }, options);
      this.root.addEventListener('mouseleave', () => {
        this.hovered = false;
        this.startAutoplay();
      }, options);
      this.root.addEventListener('focusin', () => {
        this.focusWithin = true;
        this.stopAutoplay();
      }, options);
      this.root.addEventListener('focusout', (event) => {
        if (event.relatedTarget && this.root.contains(event.relatedTarget)) return;
        this.focusWithin = false;
        this.startAutoplay();
      }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      }, options);
      reduceMotion.addEventListener('change', () => {
        this.autoplayEnabled = this.root.dataset.autoplay === 'true' && !reduceMotion.matches;
        if (reduceMotion.matches) this.stopAutoplay();
        else this.startAutoplay();
        this.update(false);
      }, options);
    }

    enqueue(direction) {
      if (this.destroyed || this.realCount < 2) return;
      this.queue.push(direction);
      this.stopAutoplay();
      if (!this.boundaryReady) return;
      this.consumeQueue();
    }

    consumeQueue() {
      if (this.moving || this.queue.length === 0 || this.destroyed) return;
      const direction = this.queue.shift();
      this.physicalIndex += direction;
      this.moving = true;
      this.update(!reduceMotion.matches);
      if (reduceMotion.matches) requestAnimationFrame(() => this.finishTransition());
    }

    update(animate) {
      if (!this.slides?.length) return;
      const activeSlide = this.slides[this.physicalIndex];
      if (!activeSlide) return;
      const offset = (this.viewport.clientWidth / 2) - (activeSlide.offsetLeft + activeSlide.offsetWidth / 2);
      this.track.classList.toggle('is-animating', Boolean(animate));
      this.track.style.transform = `translate3d(${offset}px, 0, 0)`;
      this.updateState();
    }

    updateState() {
      this.realIndex = modulo(this.physicalIndex - (this.realCount > 1 ? 1 : 0), this.realCount);
      this.slides.forEach((slide, index) => {
        slide.classList.toggle('is-active', index === this.physicalIndex);
      });
      this.realSlides.forEach((slide, index) => {
        const active = index === this.realIndex;
        slide.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (this.currentStatus) this.currentStatus.textContent = String(this.realIndex + 1);
    }

    handleTransitionEnd(event) {
      if (event.target !== this.track || event.propertyName !== 'transform' || !this.moving) return;
      this.finishTransition();
    }

    finishTransition() {
      let teleported = false;
      if (this.physicalIndex === 0) {
        teleported = true;
        this.physicalIndex = this.realCount;
      } else if (this.physicalIndex === this.realCount + 1) {
        teleported = true;
        this.physicalIndex = 1;
      }
      if (teleported) {
        this.root.classList.add('is-teleporting');
        this.update(false);
        void this.track.offsetWidth;
        this.root.classList.remove('is-teleporting');
      }
      this.moving = false;
      if (this.resizePending) {
        this.resizePending = false;
        this.update(false);
      }
      if (this.queue.length > 0) this.consumeQueue();
      else this.startAutoplay();
    }

    handleResize() {
      if (this.moving) {
        this.resizePending = true;
        return;
      }
      this.update(false);
    }

    handlePointerDown(event) {
      if (this.realCount < 2 || event.pointerType === 'mouse' && event.button !== 0) return;
      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerStartY = event.clientY;
      this.pointerDeltaX = 0;
      this.pointerDeltaY = 0;
      try {
        this.viewport.setPointerCapture?.(event.pointerId);
      } catch {
        // Synthetic Theme Editor/tests may not register an active pointer.
      }
      this.stopAutoplay();
    }

    handlePointerMove(event) {
      if (event.pointerId !== this.pointerId) return;
      this.pointerDeltaX = event.clientX - this.pointerStartX;
      this.pointerDeltaY = event.clientY - this.pointerStartY;
    }

    handlePointerEnd(event) {
      if (event.pointerId !== this.pointerId) return;
      try {
        this.viewport.releasePointerCapture?.(event.pointerId);
      } catch {
        // The pointer may already be released by the browser.
      }
      const horizontal = Math.abs(this.pointerDeltaX) > Math.abs(this.pointerDeltaY) * 1.15;
      if (horizontal && Math.abs(this.pointerDeltaX) >= 44) {
        this.enqueue(this.pointerDeltaX < 0 ? 1 : -1);
      } else {
        this.startAutoplay();
      }
      this.pointerId = null;
    }

    selectBlock(blockId) {
      const targetIndex = this.realSlides.findIndex((slide) => slide.dataset.blockId === blockId);
      if (targetIndex < 0) return;
      this.stopAutoplay();
      this.queue.length = 0;
      this.moving = false;
      this.physicalIndex = targetIndex + (this.realCount > 1 ? 1 : 0);
      this.update(false);
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.destroyed || this.realCount < 2 || this.moving || this.queue.length > 0 || this.hovered || this.focusWithin || document.hidden) return;
      this.stopAutoplay();
      this.autoplayTimer = window.setTimeout(() => this.enqueue(1), this.autoplayDelay);
    }

    stopAutoplay() {
      if (this.autoplayTimer) window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    destroy() {
      this.destroyed = true;
      this.stopAutoplay();
      this.queue.length = 0;
      this.resizeObserver?.disconnect();
      this.abortController.abort();
      this.track.querySelectorAll('[data-clone]').forEach((clone) => clone.remove());
      this.track.style.removeProperty('transform');
      this.track.classList.remove('is-animating');
      this.root.classList.remove('is-ready');
    }
  }

  const initializeSection = (scope = document) => {
    scope.querySelectorAll?.('[data-nas-carousel]').forEach((root) => {
      const sectionId = root.closest('[data-nas-hardware]')?.dataset.sectionId;
      if (!sectionId || instances.has(sectionId)) return;
      instances.set(sectionId, new NasCarousel(root));
    });
  };

  const unloadSection = (sectionId) => {
    const instance = instances.get(sectionId);
    if (!instance) return;
    instance.destroy();
    instances.delete(sectionId);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeSection(), { once: true });
  } else {
    initializeSection();
  }

  document.addEventListener('shopify:section:load', (event) => initializeSection(event.target));
  document.addEventListener('shopify:section:unload', (event) => unloadSection(event.detail.sectionId));
  document.addEventListener('shopify:block:select', (event) => {
    const sectionId = event.detail.sectionId;
    const instance = instances.get(sectionId);
    instance?.selectBlock(event.detail.blockId);
  });
})();
